import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {Button} from "primereact/button";
import {Card} from "primereact/card";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {Tag} from "primereact/tag";
import {confirmDialog} from "primereact/confirmdialog";
import {CONTENT_TYPE_UIDS} from "../../../system/content-type-registry";
import {hasAdminPermission, isSuperAdmin} from "../../../system/admin-access";
import {
    fetchDetailedPlans,
    fetchPlanEditor,
    fetchPlanPriceEditor,
    resetPlanCatalogDefaults,
    savePlanEditor,
    savePlanPriceList,
} from "../../../system/support-access-api";
import {useContentTypeData} from "../../../system/use-content-type-data";
import {useTranslate} from "../../../system/useTranslate";
import {
    DataState,
    FormDialog,
    FormField,
    RTL_DATATABLE_PROPS,
    RTL_DROPDOWN_PROPS,
    SalesSectionHeader,
    SwitchField,
    TableCard,
} from "../components/common";
import {entityKey, formatMoney, statusSeverity} from "../sales-utils";
import {EMPTY_PRICING, pricingForForm, pricingPayload} from "./plans-pricing-utils";

const EMPTY_PLAN = {
    name: "",
    code: "",
    description: "",
    subscriptionPlanStatus: "draft",
    visibility: "public",
    sortOrder: 0,
    isDefault: false,
    isCustom: false,
    badge: "",
    recommended: false,
    trialEnabled: false,
    trialDays: 0,
    marketingSummary: "",
};

const STATUS_VALUES = ["draft", "active", "archived"];
const VISIBILITY_VALUES = ["public", "private", "internal"];
const PRICING_MODEL_VALUES = ["free", "fixed", "per_user", "custom"];

function enumLabel(translate, value) {
    return translate(`billing-admin-option-${value}`);
}

function enumOptions(translate, values) {
    return values.map((value) => ({label: enumLabel(translate, value), value}));
}

function annualMonthlyEquivalent(value) {
    if (value == null || value === "") return value;
    try {
        return (window.BigInt(String(value).split(".")[0]) / window.BigInt(12)).toString();
    } catch (error) {
        return value;
    }
}

function requestMessage(error) {
    return error?.response?.data?.error?.message || error?.message || "content-type-request-error";
}

function newMarketingFeature(index) {
    return {
        documentId: null,
        title: "",
        description: "",
        category: "",
        displayValue: "",
        highlighted: false,
        sortOrder: index,
    };
}

function newCustomization(index) {
    return {
        documentId: null,
        _rowKey: `customization-${Date.now()}-${index}`,
        code: "",
        name: "",
        description: "",
        selectionMode: "single",
        isRequired: true,
        minimumSelections: 1,
        maximumSelections: 1,
        isActive: true,
        sortOrder: index,
        options: [],
    };
}

function newCustomizationOption(index) {
    return {
        documentId: null,
        _rowKey: `customization-option-${Date.now()}-${index}`,
        code: "",
        name: "",
        description: "",
        priceIncreasePercentage: 0,
        isDefault: index === 0,
        isActive: true,
        sortOrder: index,
        effects: [],
    };
}

function newCustomizationEffect(index, featureDocumentId = null) {
    return {
        documentId: null,
        _rowKey: `customization-effect-${Date.now()}-${index}`,
        featureDocumentId,
        operation: "replace",
        value: "",
    };
}

function localizedNumber(value) {
    if (value == null || value === "") return null;
    if (typeof value === "number") return value;
    const normalized = String(value)
        .trim()
        .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
        .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
        .replace(/[٬,\s]/g, "")
        .replace(/٫/g, ".");
    if (!normalized) return null;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

function effectDefaultValue(feature) {
    const valueType = feature?.feature?.valueType;
    if (valueType === "boolean") return false;
    if (["integer", "decimal", "storage", "duration"].includes(valueType)) return 0;
    if (valueType === "unlimited") return true;
    return "";
}

function validEffectValue(effect, features) {
    if (["enable", "disable"].includes(effect.operation)) return true;
    const feature = features.find((row) => row.featureDocumentId === effect.featureDocumentId);
    if (!feature) return false;
    const valueType = feature.feature?.valueType;
    if (["integer", "storage", "duration"].includes(valueType)) {
        const value = localizedNumber(effect.value);
        return Number.isInteger(value) && value >= 0;
    }
    if (valueType === "decimal") {
        const value = localizedNumber(effect.value);
        return Number.isFinite(value) && value >= 0;
    }
    if (valueType === "boolean") return typeof effect.value === "boolean";
    if (valueType === "unlimited") return effect.value === true;
    return effect.value == null || typeof effect.value === "string";
}

function effectPayloadValue(effect, features) {
    if (["enable", "disable"].includes(effect.operation)) return null;
    const feature = features.find((row) => row.featureDocumentId === effect.featureDocumentId);
    const valueType = feature?.feature?.valueType;
    if (["integer", "decimal", "storage", "duration"].includes(valueType)) {
        return localizedNumber(effect.value);
    }
    if (["boolean", "unlimited"].includes(valueType)) return effect.value === true;
    return effect.value === "" ? null : effect.value;
}

function entitlementPayloadValue(entitlement) {
    if (entitlement.isUnlimited || entitlement.isContractual) return null;
    const valueType = entitlement.feature?.valueType;
    if (["integer", "decimal", "storage", "duration"].includes(valueType)) {
        return localizedNumber(entitlement.value);
    }
    return entitlement.value;
}

function validEntitlementValue(entitlement) {
    if (entitlement.isUnlimited || entitlement.isContractual || entitlement.value == null || entitlement.value === "") return true;
    const valueType = entitlement.feature?.valueType;
    if (["integer", "storage", "duration"].includes(valueType)) {
        const value = localizedNumber(entitlement.value);
        return Number.isInteger(value) && value >= 0;
    }
    if (valueType === "decimal") {
        const value = localizedNumber(entitlement.value);
        return Number.isFinite(value) && value >= 0;
    }
    if (valueType === "boolean") return typeof entitlement.value === "boolean";
    if (valueType === "unlimited") return entitlement.value === true;
    return typeof entitlement.value === "string";
}

function CustomizationEffectValue({effect, feature, onChange, translate}) {
    if (["enable", "disable"].includes(effect.operation)) {
        return <InputText className="w-full text-xs" value="" disabled placeholder={translate("billing-admin-effect-value-not-required")}/>;
    }
    const valueType = feature?.feature?.valueType;
    if (["integer", "decimal", "storage", "duration"].includes(valueType)) {
        return (
            <InputNumber
                className="w-full"
                value={localizedNumber(effect.value)}
                min={0}
                mode="decimal"
                maxFractionDigits={valueType === "decimal" ? 4 : 0}
                onValueChange={(event) => onChange(event.value)}
            />
        );
    }
    if (["boolean", "unlimited"].includes(valueType)) {
        return <InputSwitch checked={effect.value === true} onChange={(event) => onChange(event.value)}/>;
    }
    return <InputText className="w-full text-xs" value={effect.value ?? ""} onChange={(event) => onChange(event.target.value)}/>;
}

function hydrateCustomizations(rows = []) {
    return (Array.isArray(rows) ? rows : []).map((group, groupIndex) => ({
        ...group,
        _rowKey: group.documentId || `customization-${Date.now()}-${groupIndex}`,
        options: (Array.isArray(group.options) ? group.options : []).map((option, optionIndex) => ({
            ...option,
            _rowKey: option.documentId || `customization-option-${Date.now()}-${groupIndex}-${optionIndex}`,
            effects: (Array.isArray(option.effects) ? option.effects : []).map((effect, effectIndex) => ({
                ...effect,
                _rowKey: effect.documentId || `customization-effect-${Date.now()}-${groupIndex}-${optionIndex}-${effectIndex}`,
                value: effect.value == null ? "" : effect.value,
            })),
        })),
    }));
}

function customizationPayload(rows = [], features = []) {
    return rows.map((group, groupIndex) => ({
        ...(group.documentId ? {documentId: group.documentId} : {}),
        code: String(group.code || "").trim(),
        name: String(group.name || "").trim(),
        description: group.description || null,
        selectionMode: group.selectionMode || "single",
        isRequired: group.isRequired !== false,
        minimumSelections: Number(group.minimumSelections ?? 0),
        maximumSelections: Number(group.maximumSelections ?? 1),
        isActive: group.isActive !== false,
        sortOrder: Number(group.sortOrder ?? groupIndex),
        options: (group.options || []).map((option, optionIndex) => ({
            ...(option.documentId ? {documentId: option.documentId} : {}),
            code: String(option.code || "").trim(),
            name: String(option.name || "").trim(),
            description: option.description || null,
            priceIncreasePercentage: Number(option.priceIncreasePercentage || 0),
            isDefault: option.isDefault === true,
            isActive: option.isActive !== false,
            sortOrder: Number(option.sortOrder ?? optionIndex),
            effects: (option.effects || [])
                .filter((effect) => effect.featureDocumentId)
                .map((effect) => ({
                    ...(effect.documentId ? {documentId: effect.documentId} : {}),
                    featureDocumentId: effect.featureDocumentId,
                    operation: effect.operation || "replace",
                    value: effectPayloadValue(effect, features),
                })),
        })),
    }));
}

function PlanCustomizationsEditor({rows, features, onChange, translate}) {
    const updateGroup = (groupKey, patch) => onChange(rows.map((group) => (
        group._rowKey === groupKey ? {...group, ...patch} : group
    )));
    const removeGroup = (groupKey) => onChange(rows.filter((group) => group._rowKey !== groupKey));
    const featureOptions = features
        .filter((row) => row.featureDocumentId)
        .map((row) => ({label: `${row.feature?.name || row.feature?.key} (${row.feature?.key || ""})`, value: row.featureDocumentId}));

    return (
        <div className="col-12 flex flex-column gap-3">
            <div className="flex align-items-center justify-content-between gap-2">
                <div className="flex flex-column gap-1">
                    <span className="text-sm font-bold text-900">{translate("billing-admin-plan-customizations")}</span>
                    <span className="text-xs text-600">{translate("billing-admin-plan-customizations-description")}</span>
                </div>
                <Button
                    type="button"
                    className="text-xs gap-2"
                    icon="pi pi-plus"
                    label={translate("billing-admin-add-customization")}
                    outlined
                    onClick={() => onChange([...rows, newCustomization(rows.length)])}
                />
            </div>
            {!rows.length && <Message severity="info" text={translate("billing-admin-no-customizations")}/>} 
            {rows.map((group) => (
                <Card key={group._rowKey || group.documentId} className="border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                    <div className="flex align-items-center justify-content-between gap-2 mb-3">
                        <span className="text-sm font-bold">{group.name || translate("billing-admin-new-customization")}</span>
                        <Button type="button" className="text-xs gap-2" icon="pi pi-trash" label={translate("delete")} severity="danger" text onClick={() => removeGroup(group._rowKey)}/>
                    </div>
                    <div className="grid m-0">
                        <FormField label={translate("billing-admin-customization-code")} required>
                            <InputText className="w-full text-xs ltr" value={group.code || ""} onChange={(event) => updateGroup(group._rowKey, {code: event.target.value})}/>
                        </FormField>
                        <FormField label={translate("name")} required>
                            <InputText className="w-full text-xs" value={group.name || ""} onChange={(event) => updateGroup(group._rowKey, {name: event.target.value})}/>
                        </FormField>
                        <FormField label={translate("billing-admin-selection-mode")}>
                            <Dropdown {...RTL_DROPDOWN_PROPS} value={group.selectionMode || "single"} options={enumOptions(translate, ["single", "multiple"])} onChange={(event) => updateGroup(group._rowKey, {selectionMode: event.value, maximumSelections: event.value === "single" ? 1 : group.maximumSelections})}/>
                        </FormField>
                        <FormField label={translate("billing-admin-minimum-selections")}>
                            <InputNumber className="w-full" value={group.minimumSelections ?? 1} min={0} onValueChange={(event) => updateGroup(group._rowKey, {minimumSelections: event.value})}/>
                        </FormField>
                        <FormField label={translate("billing-admin-maximum-selections")}>
                            <InputNumber className="w-full" value={group.maximumSelections ?? 1} min={1} disabled={group.selectionMode === "single"} onValueChange={(event) => updateGroup(group._rowKey, {maximumSelections: event.value})}/>
                        </FormField>
                        <FormField label={translate("billing-admin-sort")}>
                            <InputNumber className="w-full" value={group.sortOrder ?? 0} min={0} onValueChange={(event) => updateGroup(group._rowKey, {sortOrder: event.value})}/>
                        </FormField>
                        <SwitchField label={translate("billing-admin-required")} checked={group.isRequired !== false} onChange={(value) => updateGroup(group._rowKey, {isRequired: value, minimumSelections: value ? Math.max(1, group.minimumSelections || 0) : 0})}/>
                        <SwitchField label={translate("billing-admin-enabled")} checked={group.isActive !== false} onChange={(value) => updateGroup(group._rowKey, {isActive: value})}/>
                        <FormField className="col-12" label={translate("description")}>
                            <InputTextarea className="w-full text-xs" rows={2} value={group.description || ""} onChange={(event) => updateGroup(group._rowKey, {description: event.target.value})}/>
                        </FormField>
                        <div className="col-12 flex flex-column gap-2">
                            <div className="flex align-items-center justify-content-between gap-2">
                                <span className="text-xs font-bold">{translate("billing-admin-customization-options")}</span>
                                <Button type="button" className="text-xs gap-2" icon="pi pi-plus" label={translate("billing-admin-add-customization-option")} outlined onClick={() => updateGroup(group._rowKey, {options: [...(group.options || []), newCustomizationOption((group.options || []).length)]})}/>
                            </div>
                            {(group.options || []).map((option) => {
                                const updateOption = (patch) => updateGroup(group._rowKey, {
                                    options: group.options.map((item) => (
                                        item._rowKey === option._rowKey
                                            ? {...item, ...patch, ...(patch.isActive === false ? {isDefault: false} : {})}
                                            : (group.selectionMode === "single" && patch.isDefault === true
                                                ? {...item, isDefault: false}
                                                : item)
                                    )),
                                });
                                const removeOption = () => updateGroup(group._rowKey, {options: group.options.filter((item) => item._rowKey !== option._rowKey)});
                                return (
                                    <Card key={option._rowKey || option.documentId} className="surface-50 border-1 border-200 shadow-none" pt={{body: {className: "p-2"}, content: {className: "p-0"}}}>
                                        <div className="grid m-0 align-items-end">
                                            <FormField label={translate("billing-admin-option-code")} required><InputText className="w-full text-xs ltr" value={option.code || ""} onChange={(event) => updateOption({code: event.target.value})}/></FormField>
                                            <FormField label={translate("name")} required><InputText className="w-full text-xs" value={option.name || ""} onChange={(event) => updateOption({name: event.target.value})}/></FormField>
                                            <FormField label={translate("billing-admin-price-increase-percentage")}><InputNumber className="w-full" value={option.priceIncreasePercentage ?? 0} min={0} max={10000} suffix=" %" onValueChange={(event) => updateOption({priceIncreasePercentage: event.value})}/></FormField>
                                            <FormField label={translate("billing-admin-sort")}><InputNumber className="w-full" value={option.sortOrder ?? 0} min={0} onValueChange={(event) => updateOption({sortOrder: event.value})}/></FormField>
                                            <SwitchField label={translate("billing-admin-default")} checked={option.isDefault === true} onChange={(value) => updateOption({isDefault: value})}/>
                                            <SwitchField label={translate("billing-admin-enabled")} checked={option.isActive !== false} onChange={(value) => updateOption({isActive: value})}/>
                                            <Button type="button" className="text-xs gap-2" icon="pi pi-trash" label={translate("delete")} severity="danger" text onClick={removeOption}/>
                                            <FormField className="col-12" label={translate("description")}>
                                                <InputTextarea className="w-full text-xs" rows={2} value={option.description || ""} onChange={(event) => updateOption({description: event.target.value})}/>
                                            </FormField>
                                            <div className="col-12 flex flex-column gap-2 mt-2">
                                                <div className="flex align-items-center justify-content-between gap-2"><span className="text-xs font-bold">{translate("billing-admin-option-effects")}</span><Button type="button" className="text-xs gap-2" icon="pi pi-plus" label={translate("billing-admin-add-effect")} text onClick={() => updateOption({effects: [...(option.effects || []), newCustomizationEffect((option.effects || []).length)]})}/></div>
                                                {(option.effects || []).map((effect) => {
                                                    const selectedFeature = features.find((row) => row.featureDocumentId === effect.featureDocumentId);
                                                    const updateEffect = (patch) => updateOption({
                                                        effects: option.effects.map((item) => item._rowKey === effect._rowKey ? {...item, ...patch} : item),
                                                    });
                                                    return (
                                                        <div className="grid m-0 align-items-end" key={effect._rowKey || effect.documentId}>
                                                            <FormField className="col-12 md:col-5" label={translate("billing-admin-feature")} required>
                                                                <Dropdown
                                                                    {...RTL_DROPDOWN_PROPS}
                                                                    value={effect.featureDocumentId || null}
                                                                    options={featureOptions}
                                                                    onChange={(event) => {
                                                                        const nextFeature = features.find((row) => row.featureDocumentId === event.value);
                                                                        updateEffect({featureDocumentId: event.value, value: effectDefaultValue(nextFeature)});
                                                                    }}
                                                                />
                                                            </FormField>
                                                            <FormField className="col-12 md:col-3" label={translate("billing-admin-operation")}>
                                                                <Dropdown
                                                                    {...RTL_DROPDOWN_PROPS}
                                                                    value={effect.operation || "replace"}
                                                                    options={enumOptions(translate, ["replace", "increase", "decrease", "enable", "disable"])}
                                                                    onChange={(event) => updateEffect({
                                                                        operation: event.value,
                                                                        value: ["enable", "disable"].includes(event.value)
                                                                            ? null
                                                                            : (["enable", "disable"].includes(effect.operation)
                                                                                ? effectDefaultValue(selectedFeature)
                                                                                : effect.value),
                                                                    })}
                                                                />
                                                            </FormField>
                                                            <FormField className="col-12 md:col-3" label={translate("billing-admin-effect-value")}>
                                                                <CustomizationEffectValue effect={effect} feature={selectedFeature} translate={translate} onChange={(value) => updateEffect({value})}/>
                                                            </FormField>
                                                            <Button type="button" className="text-xs gap-2" icon="pi pi-trash" severity="danger" text onClick={() => updateOption({effects: option.effects.filter((item) => item._rowKey !== effect._rowKey)})}/>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </Card>
            ))}
        </div>
    );
}

function EntitlementValue({row, onChange}) {
    const disabled = row.isUnlimited || row.isContractual;
    const type = row.feature?.valueType;
    if (type === "boolean") {
        return <InputSwitch disabled={disabled} checked={Boolean(row.value)} onChange={(event) => onChange({...row, value: event.value})}/>;
    }
    if (["integer", "decimal", "storage", "duration"].includes(type)) {
        return (
            <InputNumber
                disabled={disabled}
                className="w-full"
                value={row.value}
                min={0}
                mode="decimal"
                maxFractionDigits={type === "decimal" ? 4 : 0}
                onValueChange={(event) => onChange({...row, value: event.value})}
            />
        );
    }
    return <InputText disabled={disabled} className="w-full text-xs" value={row.value ?? ""} onChange={(event) => onChange({...row, value: event.target.value})}/>;
}

function EntitlementMatrix({rows, onChange, translate}) {
    const [expandedRows, setExpandedRows] = useState(null);
    const update = (row, patch) => onChange(rows.map((item) => (
        item._rowKey === row._rowKey ? {...item, ...patch} : item
    )));
    const expansion = (row) => (
        <div className="grid m-0">
            <FormField label={translate("billing-admin-display-value")}>
                <InputText className="w-full text-xs" value={row.displayValue || ""} onChange={(event) => update(row, {displayValue: event.target.value})}/>
            </FormField>
            <FormField label={translate("billing-admin-soft-limit")}>
                <InputNumber className="w-full" value={row.softLimit} min={0} onValueChange={(event) => update(row, {softLimit: event.value})}/>
            </FormField>
            <FormField label={translate("billing-admin-hard-limit")}>
                <InputNumber className="w-full" value={row.hardLimit} min={0} onValueChange={(event) => update(row, {hardLimit: event.value})}/>
            </FormField>
            <FormField label={translate("billing-admin-overage-unit-price")}>
                <InputNumber className="w-full" value={row.overageUnitPrice} min={0} disabled={!row.overageAllowed} useGrouping={false} onValueChange={(event) => update(row, {overageUnitPrice: event.value})}/>
            </FormField>
            <SwitchField label={translate("billing-admin-overage")} checked={row.overageAllowed} onChange={(value) => update(row, {overageAllowed: value})}/>
            <FormField className="col-12" label={translate("billing-admin-notes")}>
                <InputTextarea className="w-full text-xs" rows={2} value={row.notes || ""} onChange={(event) => update(row, {notes: event.target.value})}/>
            </FormField>
        </div>
    );

    return (
        <DataTable
            {...RTL_DATATABLE_PROPS}
            dataKey="_rowKey"
            value={rows}
            expandedRows={expandedRows}
            onRowToggle={(event) => setExpandedRows(event.data)}
            emptyMessage={translate("sales-empty-data")}
            paginator
            rows={10}
            rowExpansionTemplate={expansion}
        >
            <Column expander className="text-right"/>
            <Column
                className="text-right"
                header={translate("billing-admin-feature")}
                body={(row) => (
                    <div className="flex flex-column gap-1">
                        <span className="text-xs font-bold text-900">{row.feature?.name}</span>
                        <span className="text-xs text-500 ltr text-left">{row.feature?.key}</span>
                    </div>
                )}
            />
            <Column
                className="text-right"
                header={translate("billing-admin-enabled")}
                body={(row) => <InputSwitch checked={row.enabled} onChange={(event) => update(row, {enabled: event.value})}/>}
            />
            <Column
                className="text-right"
                header={translate("billing-admin-value")}
                body={(row) => <EntitlementValue row={row} onChange={(next) => update(row, next)}/>}
            />
            <Column
                className="text-right"
                header={translate("billing-admin-unlimited")}
                body={(row) => (
                    <InputSwitch
                        checked={row.isUnlimited}
                        onChange={(event) => update(row, {
                            isUnlimited: event.value,
                            isContractual: event.value ? false : row.isContractual,
                        })}
                    />
                )}
            />
            <Column
                className="text-right"
                header={translate("billing-admin-contractual")}
                body={(row) => (
                    <InputSwitch
                        checked={row.isContractual}
                        onChange={(event) => update(row, {
                            isContractual: event.value,
                            isUnlimited: event.value ? false : row.isUnlimited,
                        })}
                    />
                )}
            />
        </DataTable>
    );
}

function MarketingFeaturesEditor({rows, onChange, translate}) {
    const update = (index, patch) => onChange(rows.map((row, rowIndex) => rowIndex === index ? {...row, ...patch} : row));
    return (
        <div className="col-12 flex flex-column gap-3">
            <div className="flex align-items-center justify-content-between gap-2">
                <span className="text-sm font-bold text-900">{translate("billing-admin-marketing-features")}</span>
                <Button
                    type="button"
                    className="text-xs gap-2"
                    icon="pi pi-plus"
                    label={translate("billing-admin-add-marketing-feature")}
                    outlined
                    onClick={() => onChange([...rows, newMarketingFeature(rows.length)])}
                />
            </div>
            {!rows.length && <span className="text-xs text-500">{translate("sales-empty-data")}</span>}
            {rows.map((row, index) => (
                <Card
                    key={row.documentId || `marketing-${index}`}
                    className="border-1 border-200 shadow-none"
                    pt={{body: {className: "p-3"}, content: {className: "p-0"}}}
                >
                    <div className="grid">
                        <FormField label={translate("name")} required>
                            <InputText className="w-full text-xs" value={row.title} onChange={(event) => update(index, {title: event.target.value})}/>
                        </FormField>
                        <FormField label={translate("billing-admin-display-value")}>
                            <InputText className="w-full text-xs" value={row.displayValue || ""} onChange={(event) => update(index, {displayValue: event.target.value})}/>
                        </FormField>
                        <FormField label={translate("billing-admin-category")}>
                            <InputText className="w-full text-xs" value={row.category || ""} onChange={(event) => update(index, {category: event.target.value})}/>
                        </FormField>
                        <FormField label={translate("billing-admin-sort")}>
                            <InputNumber className="w-full" value={row.sortOrder} min={0} onValueChange={(event) => update(index, {sortOrder: event.value})}/>
                        </FormField>
                        <SwitchField label={translate("billing-admin-highlighted")} checked={row.highlighted} onChange={(value) => update(index, {highlighted: value})}/>
                        <FormField className="col-12" label={translate("description")}>
                            <InputTextarea className="w-full text-xs" rows={2} value={row.description || ""} onChange={(event) => update(index, {description: event.target.value})}/>
                        </FormField>
                        <div className="col-12">
                            <Button
                                type="button"
                                className="text-xs gap-2"
                                icon="pi pi-trash"
                                label={translate("delete")}
                                severity="danger"
                                outlined
                                onClick={() => onChange(rows.filter((_, rowIndex) => rowIndex !== index))}
                            />
                        </div>
                    </div>
                </Card>
            ))}
        </div>
    );
}

function PricingModelEditor({value, onChange, translate}) {
    const update = (patch) => onChange({...value, configured: true, ...patch});
    const recurring = ["fixed", "per_user"].includes(value.model);
    const perUser = value.model === "per_user";
    const amountLabel = perUser
        ? translate("billing-admin-price-per-user")
        : translate("billing-admin-price-per-workspace");

    return (
        <>
            <FormField className="col-12 md:col-6" label={translate("billing-admin-payment-model")} required>
                <Dropdown
                    {...RTL_DROPDOWN_PROPS}
                    value={value.model}
                    options={enumOptions(translate, PRICING_MODEL_VALUES)}
                    onChange={(event) => update({model: event.value})}
                />
            </FormField>
            <FormField className="col-12 md:col-6" label={translate("billing-admin-currency")} required>
                <InputText
                    className="w-full text-xs ltr"
                    value={value.currency}
                    onChange={(event) => update({currency: event.target.value.toUpperCase()})}
                />
            </FormField>

            {perUser && (
                <>
                    <FormField label={translate("billing-admin-minimum-seats")}>
                        <InputNumber className="w-full" value={value.minimumSeats} min={1} onValueChange={(event) => update({minimumSeats: event.value})}/>
                    </FormField>
                    <FormField label={translate("billing-admin-maximum-seats")}>
                        <InputNumber className="w-full" value={value.maximumSeats} min={1} onValueChange={(event) => update({maximumSeats: event.value})}/>
                    </FormField>
                </>
            )}

            {recurring && (
                <div className="col-12 grid m-0">
                    <div className="col-12 md:col-6">
                        <Card className="h-full border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                            <div className="flex flex-column gap-3">
                                <div className="flex align-items-center justify-content-between gap-2">
                                    <div className="flex flex-column gap-1">
                                        <span className="text-sm font-bold text-900">{translate("billing-admin-monthly-payment")}</span>
                                        <span className="text-xs text-500">{amountLabel}</span>
                                    </div>
                                    <InputSwitch checked={value.monthlyEnabled} onChange={(event) => update({monthlyEnabled: event.value})}/>
                                </div>
                                <InputNumber
                                    className="w-full"
                                    value={value.monthlyAmount}
                                    min={0}
                                    useGrouping
                                    disabled={!value.monthlyEnabled}
                                    onValueChange={(event) => update({monthlyAmount: event.value})}
                                />
                            </div>
                        </Card>
                    </div>
                    <div className="col-12 md:col-6">
                        <Card className="h-full border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                            <div className="flex flex-column gap-3">
                                <div className="flex align-items-center justify-content-between gap-2">
                                    <div className="flex flex-column gap-1">
                                        <span className="text-sm font-bold text-900">{translate("billing-admin-annual-payment")}</span>
                                        <span className="text-xs text-500">{amountLabel}</span>
                                    </div>
                                    <InputSwitch checked={value.annualEnabled} onChange={(event) => update({annualEnabled: event.value})}/>
                                </div>
                                <InputNumber
                                    className="w-full"
                                    value={value.annualAmount}
                                    min={0}
                                    useGrouping
                                    disabled={!value.annualEnabled}
                                    onValueChange={(event) => update({annualAmount: event.value})}
                                />
                                {value.annualEnabled && value.annualAmount != null && (
                                    <span className="text-xs text-500">
                                        {translate("billing-admin-annual-monthly-equivalent")}: {formatMoney(annualMonthlyEquivalent(value.annualAmount), value.currency, translate)}
                                    </span>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            )}

            {!recurring && (
                <div className="col-12">
                    <Card className="border-1 border-200 shadow-none" pt={{body: {className: "p-3"}, content: {className: "p-0"}}}>
                        <div className="flex align-items-start gap-2">
                            <i className={`pi ${value.model === "free" ? "pi-check-circle" : "pi-comments"} text-primary mt-1`}/>
                            <div className="flex flex-column gap-1">
                                <span className="text-sm font-bold text-900">{enumLabel(translate, value.model)}</span>
                                <span className="text-xs text-500 line-height-3">
                                    {translate(value.model === "free"
                                        ? "billing-admin-free-model-description"
                                        : "billing-admin-custom-model-description")}
                                </span>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </>
    );
}

export default function PlansPricingTab() {
    const translate = useTranslate();
    const {user} = useSelector((state) => state.auth);
    const plans = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION_PLAN);
    const prices = useContentTypeData(CONTENT_TYPE_UIDS.SUBSCRIPTION_PLAN_PRICE);
    const canViewPlans = hasAdminPermission(user, "plans.view");
    const canViewPrices = hasAdminPermission(user, "plans.view")
        && hasAdminPermission(user, "prices.view");
    const canManagePlans = hasAdminPermission(user, "plans.manage")
        && hasAdminPermission(user, "features.view")
        && hasAdminPermission(user, "entitlements.view")
        && hasAdminPermission(user, "entitlements.manage");
    const canManagePrices = hasAdminPermission(user, "plans.view")
        && hasAdminPermission(user, "prices.manage");
    const canViewDetailedPlans = canViewPlans
        && hasAdminPermission(user, "prices.view")
        && hasAdminPermission(user, "features.view")
        && hasAdminPermission(user, "entitlements.view")
        && hasAdminPermission(user, "subscriptions.view");
    const canResetDefaults = isSuperAdmin(user);

    const [detailedPlans, setDetailedPlans] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [catalogError, setCatalogError] = useState(null);
    const [catalogNotice, setCatalogNotice] = useState(null);
    const [resettingDefaults, setResettingDefaults] = useState(false);

    const [planVisible, setPlanVisible] = useState(false);
    const [planOpening, setPlanOpening] = useState(false);
    const [planSaving, setPlanSaving] = useState(false);
    const [planError, setPlanError] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [planForm, setPlanForm] = useState(EMPTY_PLAN);
    const [entitlements, setEntitlements] = useState([]);
    const [marketingFeatures, setMarketingFeatures] = useState([]);
    const [customizations, setCustomizations] = useState([]);

    const [priceVisible, setPriceVisible] = useState(false);
    const [priceOpening, setPriceOpening] = useState(false);
    const [priceSaving, setPriceSaving] = useState(false);
    const [priceError, setPriceError] = useState(null);
    const [pricePlanId, setPricePlanId] = useState(null);
    const [pricePlan, setPricePlan] = useState(null);
    const [existingPrices, setExistingPrices] = useState([]);
    const [pricingForm, setPricingForm] = useState(EMPTY_PRICING);

    const loadDetailedCatalog = async () => {
        if (!canViewDetailedPlans) return null;
        const result = await fetchDetailedPlans();
        setDetailedPlans(result);
        return result;
    };

    const reload = async () => Promise.all([
        plans.reload({sort: "sortOrder:ASC"}, {all: true}),
        prices.reload({sort: "sortOrder:ASC", populate: "plan"}, {all: true}),
        loadDetailedCatalog(),
    ]);

    useEffect(() => {
        Promise.all([
            plans.ensure({sort: "sortOrder:ASC"}, {all: true}),
            prices.ensure({sort: "sortOrder:ASC", populate: "plan"}, {all: true}),
        ]).catch(() => undefined);
        if (canViewDetailedPlans) {
            setCatalogLoading(true);
            setCatalogError(null);
            loadDetailedCatalog()
                .catch((error) => setCatalogError(translate(requestMessage(error))))
                .finally(() => setCatalogLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const restoreDefaults = async () => {
        setResettingDefaults(true);
        setCatalogError(null);
        setCatalogNotice(null);
        try {
            const result = await resetPlanCatalogDefaults();
            setDetailedPlans(result);
            await Promise.all([
                plans.reload({sort: "sortOrder:ASC"}, {all: true}),
                prices.reload({sort: "sortOrder:ASC", populate: "plan"}, {all: true}),
            ]);
            setCatalogNotice({severity: "success", text: translate("billing-admin-defaults-restored")});
        } catch (error) {
            setCatalogNotice({severity: "error", text: translate(requestMessage(error))});
        } finally {
            setResettingDefaults(false);
        }
    };

    const confirmRestoreDefaults = () => confirmDialog({
        header: translate("billing-admin-restore-defaults"),
        message: translate("billing-admin-restore-defaults-confirmation"),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: translate("billing-admin-restore-defaults-accept"),
        rejectLabel: translate("cancel"),
        acceptClassName: "p-button-danger text-xs gap-2",
        rejectClassName: "p-button-text text-xs gap-2",
        className: "rtl text-xs",
        accept: restoreDefaults,
    });

    const openPlan = async (plan = null) => {
        if (!canManagePlans) return;
        setSelectedPlan(plan);
        setPlanForm(EMPTY_PLAN);
        setEntitlements([]);
        setMarketingFeatures([]);
        setCustomizations([]);
        setPlanVisible(true);
        setPlanOpening(true);
        setPlanError(null);
        try {
            const editor = await fetchPlanEditor(plan ? entityKey(plan) : null);
            setPlanForm({...EMPTY_PLAN, ...(editor?.plan || {})});
            setEntitlements((editor?.entitlements || []).map((entitlement) => ({
                ...entitlement,
                _rowKey: entitlement.featureDocumentId,
                isNew: false,
            })));
            setMarketingFeatures(editor?.marketingFeatures || []);
            setCustomizations(hydrateCustomizations(editor?.customizations || []));
        } catch (error) {
            setPlanError(translate(requestMessage(error)));
        } finally {
            setPlanOpening(false);
        }
    };

    const savePlan = async (event) => {
        event?.preventDefault();
        if (!entitlements.length) {
            setPlanError(translate("billing-admin.error.feature-catalog-empty"));
            return;
        }
        const invalidEntitlement = entitlements.find((entitlement) => !validEntitlementValue(entitlement));
        if (invalidEntitlement) {
            setPlanError(`${translate("billing-admin.error.entitlement-value-invalid")}: ${invalidEntitlement.feature?.name || invalidEntitlement.feature?.key}`);
            return;
        }
        if (marketingFeatures.some((feature) => !feature.title.trim())) {
            setPlanError(translate("billing-admin.error.marketing-feature-title-required"));
            return;
        }
        for (const group of customizations) {
            if (!String(group.code || "").trim() || !String(group.name || "").trim()) {
                setPlanError(translate("billing-admin.error.customization-fields-required"));
                return;
            }
            const options = group.options || [];
            if (group.isActive !== false && !options.some((option) => option.isActive !== false)) {
                setPlanError(translate("billing-admin.error.customization-options-empty"));
                return;
            }
            if (group.selectionMode === "single" && Number(group.maximumSelections ?? 1) !== 1) {
                setPlanError(translate("billing-admin.error.customization-single-max"));
                return;
            }
            if (group.selectionMode === "single" && options.filter((option) => option.isActive !== false && option.isDefault === true).length !== 1) {
                setPlanError(translate("billing-admin.error.customization-default-required"));
                return;
            }
            for (const option of options) {
                if (!String(option.code || "").trim() || !String(option.name || "").trim()) {
                    setPlanError(translate("billing-admin.error.customization-option-fields-required"));
                    return;
                }
                if ((option.effects || []).some((effect) => !effect.featureDocumentId)) {
                    setPlanError(translate("billing-admin.error.customization-effect-feature-required"));
                    return;
                }
                if ((option.effects || []).some((effect) => !validEffectValue(effect, entitlements))) {
                    setPlanError(translate("billing-admin.error.customization-effect-value-invalid"));
                    return;
                }
            }
        }
        setPlanSaving(true);
        setPlanError(null);
        try {
            const plan = {
                ...planForm,
                name: planForm.name.trim(),
                code: planForm.code.trim(),
                description: planForm.description || null,
                badge: planForm.badge || null,
                sortOrder: Number(planForm.sortOrder || 0),
                trialDays: Number(planForm.trialDays || 0),
                marketingSummary: planForm.marketingSummary || null,
            };
            await savePlanEditor(
                selectedPlan ? entityKey(selectedPlan) : null,
                plan,
                entitlements.map((entitlement) => ({
                    featureDocumentId: entitlement.featureDocumentId,
                    enabled: entitlement.enabled,
                    value: entitlementPayloadValue(entitlement),
                    isUnlimited: entitlement.isUnlimited,
                    isContractual: entitlement.isContractual,
                    softLimit: entitlement.softLimit,
                    hardLimit: entitlement.hardLimit,
                    overageAllowed: entitlement.overageAllowed,
                    overageUnitPrice: entitlement.overageUnitPrice,
                    displayValue: entitlement.displayValue,
                    notes: entitlement.notes,
                })),
                marketingFeatures,
                customizationPayload(customizations, entitlements)
            );
            setPlanVisible(false);
            await reload();
        } catch (error) {
            setPlanError(translate(requestMessage(error)));
        } finally {
            setPlanSaving(false);
        }
    };

    const loadPricePlan = async (documentId) => {
        setPricePlanId(documentId);
        setPriceOpening(true);
        setPriceError(null);
        try {
            const editor = await fetchPlanPriceEditor(documentId);
            const editorPrices = Array.isArray(editor?.prices) ? editor.prices : [];
            setPricePlan(editor?.plan || null);
            setExistingPrices(editorPrices);
            setPricingForm({...pricingForForm(editorPrices), configured: true});
        } catch (error) {
            setPricePlan(null);
            setExistingPrices([]);
            setPricingForm(EMPTY_PRICING);
            setPriceError(translate(requestMessage(error)));
        } finally {
            setPriceOpening(false);
        }
    };

    const openPriceList = async (plan = null) => {
        if (!canManagePrices) return;
        setPricePlan(null);
        setExistingPrices([]);
        setPricingForm(EMPTY_PRICING);
        setPriceError(null);
        setPriceVisible(true);
        if (plan) await loadPricePlan(entityKey(plan));
        else setPricePlanId(null);
    };

    const savePrices = async (event) => {
        event?.preventDefault();
        if (!pricePlanId) return;
        setPriceSaving(true);
        setPriceError(null);
        try {
            await savePlanPriceList(
                pricePlanId,
                pricingPayload(pricingForm, existingPrices, pricePlan)
            );
            setPriceVisible(false);
            await reload();
        } catch (error) {
            setPriceError(translate(requestMessage(error)));
        } finally {
            setPriceSaving(false);
        }
    };

    const catalogPlans = detailedPlans || plans.items;
    const catalogPrices = detailedPlans
        ? detailedPlans.flatMap((plan) => plan.prices || [])
        : prices.items;
    const planOptions = catalogPlans.map((plan) => ({label: plan.name, value: entityKey(plan)}));
    const priceRowsForPlan = (plan) => catalogPrices.filter((price) => (
        String(entityKey(price.plan)) === String(entityKey(plan))
            || (detailedPlans && (plan.prices || []).some((candidate) => entityKey(candidate) === entityKey(price)))
    ));
    const pricingSummaryForPlan = (plan) => pricingForForm(priceRowsForPlan(plan));
    const pricingAmountLabel = (pricing, cadence) => {
        if (!pricing.configured) return "-";
        if (pricing.model === "free") return translate("billing-admin-option-free");
        if (pricing.model === "custom") return translate("billing-admin-contractual");
        const enabled = cadence === "month" ? pricing.monthlyEnabled : pricing.annualEnabled;
        const amount = cadence === "month" ? pricing.monthlyAmount : pricing.annualAmount;
        if (!enabled || amount == null) return "-";
        const unit = pricing.model === "per_user"
            ? translate("billing-admin-unit-per-user")
            : translate("billing-admin-unit-per-workspace");
        return `${formatMoney(amount, pricing.currency, translate)} / ${unit}`;
    };
    const planSubmitDisabled = planOpening
        || !planForm.name.trim()
        || !planForm.code.trim();
    const recurringPricing = ["fixed", "per_user"].includes(pricingForm.model);
    const priceSubmitDisabled = !pricePlanId
        || priceOpening
        || !pricePlan
        || !pricingForm.currency.trim()
        || (recurringPricing && !pricingForm.monthlyEnabled && !pricingForm.annualEnabled)
        || (recurringPricing && pricingForm.monthlyEnabled && pricingForm.monthlyAmount == null)
        || (recurringPricing && pricingForm.annualEnabled && pricingForm.annualAmount == null)
        || (pricingForm.model === "per_user" && Number(pricingForm.minimumSeats || 0) < 1)
        || (
            pricingForm.maximumSeats != null
            && Number(pricingForm.maximumSeats) < Number(pricingForm.minimumSeats || 0)
        );

    return (
        <div className="rtl flex flex-column gap-4">
            {catalogNotice && <Message className="w-full text-xs" severity={catalogNotice.severity} text={catalogNotice.text}/>} 
            {canViewPlans && (
                <div className="flex flex-column gap-3">
                    <SalesSectionHeader
                        title={translate("billing-admin-plan-builder")}
                        description={translate("billing-admin-plan-builder-description")}
                        actions={(
                            <>
                                {canResetDefaults && (
                                    <Button
                                        className="text-xs gap-2"
                                        icon="pi pi-refresh"
                                        label={translate("billing-admin-restore-defaults")}
                                        severity="danger"
                                        outlined
                                        loading={resettingDefaults}
                                        disabled={resettingDefaults}
                                        onClick={confirmRestoreDefaults}
                                    />
                                )}
                                {canManagePlans && (
                                    <Button className="text-xs gap-2" icon="pi pi-plus" label={translate("billing-admin-create-plan")} loading={planOpening} onClick={() => openPlan()}/>
                                )}
                            </>
                        )}
                    />
                    <TableCard>
                        <DataState loading={plans.loading || catalogLoading} error={catalogError || plans.error} empty={!catalogPlans.length}>
                            <DataTable {...RTL_DATATABLE_PROPS} value={catalogPlans} paginator rows={10} selectionMode="single" onRowDoubleClick={canManagePlans ? (event) => openPlan(event.data) : undefined}>
                                <Column className="text-right" field="name" header={translate("name")}/>
                                <Column className="text-right" field="code" header={translate("sales-code")}/>
                                <Column className="text-right" header={translate("sales-status")} body={(row) => <Tag severity={statusSeverity(row.subscriptionPlanStatus)} value={enumLabel(translate, row.subscriptionPlanStatus)}/>}/>
                                <Column className="text-right" header={translate("billing-admin-visibility")} body={(row) => enumLabel(translate, row.visibility)}/>
                                {canManagePlans && <Column className="text-right" body={(row) => <Button className="text-xs gap-2" icon="pi pi-pencil" text onClick={() => openPlan(row)}/>}/>}
                            </DataTable>
                        </DataState>
                    </TableCard>
                </div>
            )}

            {canViewPrices && (
                <div className="flex flex-column gap-3">
                    <SalesSectionHeader
                        title={translate("billing-admin-price-list-builder")}
                        description={translate("billing-admin-price-list-builder-description")}
                        actions={canManagePrices ? (
                            <Button className="text-xs gap-2" icon="pi pi-plus" label={translate("billing-admin-create-price-list")} onClick={() => openPriceList()}/>
                        ) : null}
                    />
                    <TableCard>
                        <DataState loading={plans.loading || prices.loading || catalogLoading} error={catalogError || plans.error || prices.error} empty={!catalogPlans.length}>
                            <DataTable {...RTL_DATATABLE_PROPS} value={catalogPlans} paginator rows={10} selectionMode="single" onRowDoubleClick={canManagePrices ? (event) => openPriceList(event.data) : undefined}>
                                <Column className="text-right" field="name" header={translate("billing-admin-plan")}/>
                                <Column
                                    className="text-right"
                                    header={translate("billing-admin-payment-model")}
                                    body={(row) => {
                                        const pricing = pricingSummaryForPlan(row);
                                        return pricing.configured
                                            ? <Tag value={enumLabel(translate, pricing.model)}/>
                                            : "-";
                                    }}
                                />
                                <Column
                                    className="text-right"
                                    header={translate("billing-admin-monthly-payment")}
                                    body={(row) => pricingAmountLabel(pricingSummaryForPlan(row), "month")}
                                />
                                <Column
                                    className="text-right"
                                    header={translate("billing-admin-annual-payment")}
                                    body={(row) => pricingAmountLabel(pricingSummaryForPlan(row), "year")}
                                />
                                <Column
                                    className="text-right"
                                    header={translate("billing-admin-minimum-seats")}
                                    body={(row) => {
                                        const pricing = pricingSummaryForPlan(row);
                                        return pricing.configured && pricing.model === "per_user"
                                            ? pricing.minimumSeats
                                            : "-";
                                    }}
                                />
                                {canManagePrices && <Column className="text-right" body={(row) => <Button className="text-xs gap-2" icon="pi pi-list" text onClick={() => openPriceList(row)}/>}/>}
                            </DataTable>
                        </DataState>
                    </TableCard>
                </div>
            )}

            <FormDialog
                visible={planVisible}
                title={translate(selectedPlan ? "billing-admin-edit-plan" : "billing-admin-create-plan")}
                description={translate("billing-admin-plan-capabilities-hint")}
                saving={planSaving || planOpening}
                error={planError}
                submitDisabled={planSubmitDisabled}
                onHide={() => setPlanVisible(false)}
                onSubmit={savePlan}
                className="rtl w-11 lg:w-10 xl:w-9 border-round-bottom-2xl"
            >
                <FormField label={translate("name")} required><InputText className="w-full text-xs" value={planForm.name} onChange={(event) => setPlanForm({...planForm, name: event.target.value})}/></FormField>
                <FormField label={translate("sales-code")} required><InputText className="w-full text-xs ltr" value={planForm.code} onChange={(event) => setPlanForm({...planForm, code: event.target.value})}/></FormField>
                <FormField label={translate("sales-status")}><Dropdown {...RTL_DROPDOWN_PROPS} value={planForm.subscriptionPlanStatus} options={enumOptions(translate, STATUS_VALUES)} onChange={(event) => setPlanForm({...planForm, subscriptionPlanStatus: event.value})}/></FormField>
                <FormField label={translate("billing-admin-visibility")}><Dropdown {...RTL_DROPDOWN_PROPS} value={planForm.visibility} options={enumOptions(translate, VISIBILITY_VALUES)} onChange={(event) => setPlanForm({...planForm, visibility: event.value})}/></FormField>
                <FormField label={translate("billing-admin-badge")}><InputText className="w-full text-xs" value={planForm.badge || ""} onChange={(event) => setPlanForm({...planForm, badge: event.target.value})}/></FormField>
                <FormField label={translate("billing-admin-sort")}><InputNumber className="w-full" value={planForm.sortOrder} min={0} onValueChange={(event) => setPlanForm({...planForm, sortOrder: event.value})}/></FormField>
                <FormField label={translate("billing-admin-trial-days")}><InputNumber className="w-full" value={planForm.trialDays} min={0} disabled={!planForm.trialEnabled} onValueChange={(event) => setPlanForm({...planForm, trialDays: event.value})}/></FormField>
                <SwitchField label={translate("billing-admin-default")} checked={planForm.isDefault} onChange={(value) => setPlanForm({...planForm, isDefault: value})}/>
                <SwitchField label={translate("billing-admin-custom")} checked={planForm.isCustom} onChange={(value) => setPlanForm({...planForm, isCustom: value})}/>
                <SwitchField label={translate("billing-admin-recommended")} checked={planForm.recommended} onChange={(value) => setPlanForm({...planForm, recommended: value})}/>
                <SwitchField label={translate("billing-admin-trial")} checked={planForm.trialEnabled} onChange={(value) => setPlanForm({...planForm, trialEnabled: value, trialDays: value ? planForm.trialDays : 0})}/>
                <FormField className="col-12" label={translate("description")}><InputTextarea className="w-full text-xs" rows={3} value={planForm.description || ""} onChange={(event) => setPlanForm({...planForm, description: event.target.value})}/></FormField>
                <FormField className="col-12" label={translate("billing-admin-marketing-summary")}><InputTextarea className="w-full text-xs" rows={2} value={planForm.marketingSummary || ""} onChange={(event) => setPlanForm({...planForm, marketingSummary: event.target.value})}/></FormField>
                <div className="col-12 flex flex-column gap-2">
                    <div className="flex align-items-center justify-content-between gap-2">
                        <span className="text-sm font-bold text-900">{translate("billing-admin-plan-capabilities")}</span>
                    </div>
                    <EntitlementMatrix rows={entitlements} onChange={setEntitlements} translate={translate}/>
                </div>
                <MarketingFeaturesEditor rows={marketingFeatures} onChange={setMarketingFeatures} translate={translate}/>
                <PlanCustomizationsEditor rows={customizations} features={entitlements} onChange={setCustomizations} translate={translate}/>
            </FormDialog>

            <FormDialog
                visible={priceVisible}
                title={translate("billing-admin-price-list-builder")}
                description={translate("billing-admin-price-list-dialog-description")}
                saving={priceSaving || priceOpening}
                error={priceError}
                submitDisabled={priceSubmitDisabled}
                onHide={() => setPriceVisible(false)}
                onSubmit={savePrices}
                className="rtl w-11 lg:w-10 xl:w-9 border-round-bottom-2xl"
            >
                <FormField className="col-12" label={translate("billing-admin-plan")} required>
                    <Dropdown {...RTL_DROPDOWN_PROPS} value={pricePlanId} options={planOptions} disabled={priceSaving || priceOpening} onChange={(event) => loadPricePlan(event.value)}/>
                </FormField>
                <PricingModelEditor value={pricingForm} onChange={setPricingForm} translate={translate}/>
            </FormDialog>
        </div>
    );
}
